import { useState } from 'react';
import { Button, Popover, PopoverContent, Theme, Toggle } from '@carbon/react';
import { Notification } from '@carbon/react/icons';
import ErrorToast from 'components/error-toast';
import type { AreaSubscription, SubscriptionTopic } from 'types/area-geometry';
import style from './area-watch-button.module.scss';

interface AreaWatchButtonProps {
  subscription: AreaSubscription;
  busy: boolean;
  error: string | null;
  onTopicsChange: (topics: SubscriptionTopic[]) => void;
  onErrorClose: () => void;
}

// "New Intersecting Data" covers both digest topics, so it is on when either is.
const OPTIONS: { key: string; label: string; topics: SubscriptionTopic[] }[] = [
  { key: 'changes', label: 'Changes to Area geometry', topics: ['changes'] },
  {
    key: 'focusItems',
    label: 'New Intersecting Data',
    topics: ['new_wells', 'new_documents'],
  },
];

/**
 * Watch button in the Area Details header that opens the "Notify me about" toggles.
 *
 * @param {AreaWatchButtonProps} props - The area's subscription, save state and handlers.
 * @return {JSX.Element} The watch button, its popover and any save failure toast.
 */
export function AreaWatchButton({
  subscription,
  busy,
  error,
  onTopicsChange,
  onErrorClose,
}: AreaWatchButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {error ? (
        <ErrorToast
          message={error}
          onClose={() => {
            onErrorClose();
            return true;
          }}
        />
      ) : null}
      <Popover
        open={open}
        align="bottom-right"
        caret={false}
        dropShadow
        onRequestClose={() => setOpen(false)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Escape') setOpen(false);
        }}
      >
        {/* Same Watch / Watching text button as the Feedback page. */}
        <Button
          kind="primary"
          size="md"
          renderIcon={Notification}
          title={subscription.subscribed ? 'Watching area' : 'Watch area'}
          aria-expanded={open}
          onClick={() => setOpen((isOpen) => !isOpen)}
        >
          {subscription.subscribed ? 'Watching' : 'Watch'}
        </Button>
        <PopoverContent>
          <Theme theme="g90" className={style.content}>
            <p className={style.title}>Notify me about</p>
            {OPTIONS.map(({ key, label, topics }) => (
              <Toggle
                key={key}
                id={`area-watch-${key}`}
                size="sm"
                labelText={label}
                hideLabel
                readOnly={busy}
                toggled={topics.some((topic) =>
                  subscription.topics.includes(topic)
                )}
                onToggle={(checked: boolean) => {
                  const rest = subscription.topics.filter(
                    (topic) => !topics.includes(topic)
                  );
                  onTopicsChange(checked ? [...rest, ...topics] : rest);
                }}
              />
            ))}
          </Theme>
        </PopoverContent>
      </Popover>
    </>
  );
}
